import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";

export interface UserGoalsRecord {
  id?: string;
  userId?: string;
  targetCeNclc: number;
  targetCoNclc: number;
  targetEeNclc: number;
  targetEoNclc: number;
  targetCeScore?: number | null;
  targetCoScore?: number | null;
  targetEeScore?: number | null;
  targetEoScore?: number | null;
  weeklyStudyHours: number;
  examDate?: string | null;
  metadata?: any;
  createdAt?: string;
  updatedAt?: string;
}

const DEFAULT_RECORD: UserGoalsRecord = {
  targetCeNclc: 7,
  targetCoNclc: 7,
  targetEeNclc: 7,
  targetEoNclc: 7,
  weeklyStudyHours: 7,
  examDate: null
};

class UserGoalService {
  private getLocalGoals(): UserGoalsRecord {
    const saved = localStorage.getItem("tef_user_goals_v2");
    if (!saved) return DEFAULT_RECORD;
    try {
      return JSON.parse(saved);
    } catch {
      return DEFAULT_RECORD;
    }
  }

  private setLocalGoals(goals: UserGoalsRecord): UserGoalsRecord {
    localStorage.setItem("tef_user_goals_v2", JSON.stringify(goals));
    return goals;
  }

  async getUserGoals(): Promise<UserGoalsRecord> {
    if (!isSupabaseConfigured || !supabase) {
      return this.getLocalGoals();
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return this.getLocalGoals();
      }
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", userData.user.id)
        .maybeSingle();

      if (error) {
        console.error("[UserGoalService] Error fetching goals", error);
        return this.getLocalGoals();
      }

      if (!data) {
        return this.createDefaultUserGoals();
      }

      return {
        id: data.id,
        userId: data.user_id,
        targetCeNclc: data.target_ce_nclc ?? 7,
        targetCoNclc: data.target_co_nclc ?? 7,
        targetEeNclc: data.target_ee_nclc ?? 7,
        targetEoNclc: data.target_eo_nclc ?? 7,
        targetCeScore: data.target_ce_score,
        targetCoScore: data.target_co_score,
        targetEeScore: data.target_ee_score,
        targetEoScore: data.target_eo_score,
        weeklyStudyHours: data.weekly_study_hours ?? 7,
        examDate: data.exam_date,
        metadata: data.metadata,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (err) {
      console.error("[UserGoalService] Exception fetching goals", err);
      return this.getLocalGoals();
    }
  }

  async createDefaultUserGoals(): Promise<UserGoalsRecord> {
    if (!isSupabaseConfigured || !supabase) {
      return this.setLocalGoals(DEFAULT_RECORD);
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return this.setLocalGoals(DEFAULT_RECORD);
      }

      const payload = {
        user_id: userData.user.id,
        target_ce_nclc: 7,
        target_co_nclc: 7,
        target_ee_nclc: 7,
        target_eo_nclc: 7,
        weekly_study_hours: 7
      };

      const { data, error } = await supabase
        .from("user_goals")
        .insert(payload)
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        targetCeNclc: data.target_ce_nclc,
        targetCoNclc: data.target_co_nclc,
        targetEeNclc: data.target_ee_nclc,
        targetEoNclc: data.target_eo_nclc,
        weeklyStudyHours: data.weekly_study_hours,
        examDate: data.exam_date,
        metadata: data.metadata
      };
    } catch (err) {
      console.error("[UserGoalService] Exception creating default goals", err);
      return this.setLocalGoals(DEFAULT_RECORD);
    }
  }

  async updateUserGoals(partialGoals: Partial<UserGoalsRecord>): Promise<UserGoalsRecord> {
    return this.upsertUserGoals(partialGoals);
  }

  async upsertUserGoals(goals: Partial<UserGoalsRecord>): Promise<UserGoalsRecord> {
    if (!isSupabaseConfigured || !supabase) {
      const current = this.getLocalGoals();
      const updated = { ...current, ...goals };
      return this.setLocalGoals(updated);
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        const current = this.getLocalGoals();
        return this.setLocalGoals({ ...current, ...goals });
      }

      // Check if exists
      const { data: existing } = await supabase
        .from("user_goals")
        .select("id")
        .eq("user_id", userData.user.id)
        .maybeSingle();

      const payload: any = {
        user_id: userData.user.id,
        ...(goals.targetCeNclc !== undefined && { target_ce_nclc: goals.targetCeNclc }),
        ...(goals.targetCoNclc !== undefined && { target_co_nclc: goals.targetCoNclc }),
        ...(goals.targetEeNclc !== undefined && { target_ee_nclc: goals.targetEeNclc }),
        ...(goals.targetEoNclc !== undefined && { target_eo_nclc: goals.targetEoNclc }),
        ...(goals.targetCeScore !== undefined && { target_ce_score: goals.targetCeScore }),
        ...(goals.targetCoScore !== undefined && { target_co_score: goals.targetCoScore }),
        ...(goals.targetEeScore !== undefined && { target_ee_score: goals.targetEeScore }),
        ...(goals.targetEoScore !== undefined && { target_eo_score: goals.targetEoScore }),
        ...(goals.weeklyStudyHours !== undefined && { weekly_study_hours: goals.weeklyStudyHours }),
        ...(goals.examDate !== undefined && { exam_date: goals.examDate }),
        ...(goals.metadata !== undefined && { metadata: goals.metadata })
      };

      if (existing?.id) {
        const { data, error } = await supabase
          .from("user_goals")
          .update(payload)
          .eq("id", existing.id)
          .select()
          .single();

        if (error) throw error;
        return this.mapRow(data);
      } else {
        const { data, error } = await supabase
          .from("user_goals")
          .insert(payload)
          .select()
          .single();

        if (error) throw error;
        return this.mapRow(data);
      }
    } catch (err) {
      console.error("[UserGoalService] Exception upserting goals", err);
      const current = this.getLocalGoals();
      return this.setLocalGoals({ ...current, ...goals });
    }
  }

  async getOnboardingStatus() {
    const goals = await this.getUserGoals();
    
    // Check if explicitly set to completed or false
    if (goals.metadata?.onboardingCompleted !== undefined && goals.metadata?.onboardingCompleted !== null) {
      return {
        onboardingCompleted: Boolean(goals.metadata.onboardingCompleted),
        mainFocus: goals.metadata.mainFocus ?? null,
        diagnosticCompleted: Boolean(goals.metadata.diagnosticCompleted)
      };
    }

    // Automated legacy user check
    let isLegacyUser = false;
    try {
      const localHistory = JSON.parse(localStorage.getItem("tef_test_history") || "[]");
      const localErrors = JSON.parse(localStorage.getItem("tef_writing_errors") || "[]");
      const localCards = JSON.parse(localStorage.getItem("tef_custom_flashcards") || "[]");
      if (localHistory.length > 0 || localErrors.length > 0 || localCards.length > 0) {
        isLegacyUser = true;
      }
    } catch (e) {
      console.error("[UserGoalService] Error parsing local storage for legacy check", e);
    }

    if (!isLegacyUser && isSupabaseConfigured && supabase) {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (userData?.user) {
          const { count: testCount } = await supabase
            .from("test_history")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userData.user.id);
          
          const { count: errorCount } = await supabase
            .from("error_bank")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userData.user.id);

          const { count: cardCount } = await supabase
            .from("flashcards")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userData.user.id);

          if ((testCount || 0) > 0 || (errorCount || 0) > 0 || (cardCount || 0) > 0) {
            isLegacyUser = true;
          }
        }
      } catch (dbErr) {
        console.error("[UserGoalService] DB legacy check failed", dbErr);
      }
    }

    if (isLegacyUser) {
      // Save onboardingCompleted: true in metadata so subsequent checks are fast
      const currentMeta = goals.metadata || {};
      const newMeta = {
        ...currentMeta,
        onboardingCompleted: true,
        isLegacyBypass: true,
        onboardingCompletedAt: new Date().toISOString()
      };
      await this.upsertUserGoals({ metadata: newMeta });
      return {
        onboardingCompleted: true,
        mainFocus: goals.metadata?.mainFocus ?? null,
        diagnosticCompleted: Boolean(goals.metadata?.diagnosticCompleted)
      };
    }

    return {
      onboardingCompleted: false,
      mainFocus: null,
      diagnosticCompleted: false
    };
  }

  async updateOnboardingMetadata(partialMetadata: any) {
    const goals = await this.getUserGoals();
    const currentMeta = goals.metadata || {};
    const newMeta = { ...currentMeta, ...partialMetadata };
    return this.upsertUserGoals({ metadata: newMeta });
  }

  async completeOnboarding(data: { mainFocus: string; studyGoal?: string; targetNclc?: number; examDate?: string | null }) {
    const goals = await this.getUserGoals();
    const currentMeta = goals.metadata || {};
    
    let targetNclc = data.targetNclc ?? goals.targetCeNclc;
    if (data.studyGoal) {
      if (data.studyGoal === "nclc7") targetNclc = 7;
      if (data.studyGoal === "nclc8") targetNclc = 8;
      if (data.studyGoal === "nclc9+") targetNclc = 9;
    }

    const newMeta = {
      ...currentMeta,
      onboardingCompleted: true,
      mainFocus: data.mainFocus,
      studyGoal: data.studyGoal || `nclc${targetNclc}`,
      diagnosticCompleted: false,
      onboardingCompletedAt: new Date().toISOString()
    };

    return this.upsertUserGoals({
      targetCeNclc: targetNclc,
      targetCoNclc: targetNclc,
      targetEeNclc: targetNclc,
      targetEoNclc: targetNclc,
      examDate: data.examDate !== undefined ? data.examDate : goals.examDate,
      metadata: newMeta
    });
  }

  private mapRow(data: any): UserGoalsRecord {
    return {
      id: data.id,
      userId: data.user_id,
      targetCeNclc: data.target_ce_nclc ?? 7,
      targetCoNclc: data.target_co_nclc ?? 7,
      targetEeNclc: data.target_ee_nclc ?? 7,
      targetEoNclc: data.target_eo_nclc ?? 7,
      targetCeScore: data.target_ce_score,
      targetCoScore: data.target_co_score,
      targetEeScore: data.target_ee_score,
      targetEoScore: data.target_eo_score,
      weeklyStudyHours: data.weekly_study_hours ?? 7,
      examDate: data.exam_date,
      metadata: data.metadata,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  }
}

export const userGoalService = new UserGoalService();
